import { useEffect, useState } from 'react';

import {
  headersObjetosContratos as headers,
  valoresExibidosOffCanvasContratos as valoresExibidos,
} from './auxiliarObjeto';
import BarraInferiorBotoes from '../../../../../components/BarraInferiorBotoes';
import BoxConteudo from '../../../../../components/BoxConteudo';
import CustomButton from '../../../../../components/CustomButton';
import CustomCol from '../../../../../components/CustomCol';
import CustomOffcanvas from '../../../../../components/CustomOffcanvas';
import CustomRow from '../../../../../components/CustomRow';
import Input from '../../../../../components/InputNew';
import InputMoney from '../../../../../components/InputNew/InputMoney';
import Select from '../../../../../components/InputNew/Select';
import Log from '../../../../../components/Log/Log';
import Modal from '../../../../../components/Modal';
import Tabela from '../../../../../components/Tabela';
import WorkspaceBotoes from '../../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../../components/WorkspaceFicha';
import {
  EnumOption,
  LogData,
  LogInterface,
} from '../../../../../modules/types/types';
import {
  getAllObjetoByContrato,
  handleCreateObjetoContrato,
  handleDeleteObjetoContrato,
  handleUpdateObjetoContrato,
} from '../../../../../services/contratos/objetoContratoService';
import { isHttpError } from '../../../../../services/httpClient';
import { getProdutosDoKit } from '../../../../../services/produtosDAF/kitProdutoService';
import { getProdutosByDenominacaoGenericaId } from '../../../../../services/produtosDAF/produtosDafService';
import theme from '../../../../../theme';
import { ENTITY } from '../../../../../utils/entityNames';
import {
  formataNomeMeioSiglas,
  formatDate,
  formatValorDinheiro,
  sanitizarForm,
} from '../../../../../utils/functions';
import {
  swalAttention,
  swalAtualizacao,
  swalDelete,
  swalDeleteConfirm,
  swalErro,
  swalSave,
} from '../../../../../utils/swal';
import { IParcelaContrato } from '../parcela';

interface ObjetoContratoProps {
  fichaID: string;
  userID: number;
  btDisable?: boolean;
  contratoData: any;
  onDataChange: () => void;
}

export interface IObjetoContrato extends Partial<LogInterface> {
  id?: number;
  usuarioId: number;
  nObjeto: number;
  produtoId: number;
  produtoDAF?: any;
  contratoId: number;
  contrato?: any;
  nParcelas: number;
  parcelas?: IParcelaContrato[];
  fatorEmbalagem: string;
  qtdContratada?: number;
  qtdDoada?: number;
  qtdTotal?: number;
  valorUnitario: string;
  valorUnitarioReequilibrio?: number;
  valorTotal?: string;
  observacoes: string;
}

const tbEntity = ENTITY.ENTITY_Contrato_Objeto;

export const ObjetoContrato = ({ ...props }: ObjetoContratoProps) => {
  const [updating, setUpdating] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  // Data
  const [data, setData] = useState<IObjetoContrato[]>();
  const [logData, setLogData] = useState<LogData>();
  const [numeroProximoItem, setNumeroProximoItem] = useState<number>(1);
  const [produtosDAFOptions, setProdutosDAFOptions] = useState<EnumOption[]>([
    { value: '0', label: 'Não informado' },
  ]);
  const [form, setForm] = useState<IObjetoContrato>({
    id: 0,
    nObjeto: 0,
    produtoId: 0,
    contratoId: 0,
    nParcelas: 0,
    fatorEmbalagem: '',
    qtdContratada: 0,
    qtdDoada: 0,
    qtdTotal: 0,
    valorUnitario: '',
    valorTotal: '',
    observacoes: '',

    contrato: null,
    parcelas: [],
    produtoDAF: null,
    usuarioId: props.userID,
  });

  // Fetch
  const fetchProdutos = async () => {
    try {
      if (props.fichaID !== 'undefined') {
        let produtosData;
        const newOptions = [...produtosDAFOptions]; // Inicia com os valores existentes

        if (props.contratoData.denominacaoGenericaId != null) {
          produtosData = await getProdutosByDenominacaoGenericaId(
            props.contratoData.denominacaoGenericaId,
          );
          newOptions.push(
            ...produtosData.map((item: any) => ({
              value: item.id,
              label: item.produtoFarmaceutico,
            })),
          );
        } else {
          produtosData = await getProdutosDoKit(
            props.contratoData.kitProdutoDAFId.toString(),
          );
          newOptions.push(
            ...produtosData.map((item: any) => ({
              value: item.produtoDAF.id,
              label: item.produtoDAF.produtoFarmaceutico,
            })),
          );
        }

        const uniqueOptions = Array.from(
          new Map(newOptions.map((item) => [item.value, item])).values(),
        );

        setProdutosDAFOptions(uniqueOptions);
      }
    } catch (error) {
      console.error('Erro ao obter produtos:', error);
      setProdutosDAFOptions([{ value: '0', label: 'Não informado' }]);
    }
  };

  const fetchData = async () => {
    try {
      const objetos = await getAllObjetoByContrato(props.fichaID);

      setData(objetos);
    } catch (error) {
      console.error('Erro ao obter dados: ', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [props.fichaID]);

  useEffect(() => {
    fetchProdutos();
  }, [props.contratoData, props.fichaID]);

  useEffect(() => {
    handleAtualizarProxItem();
  }, [data]);

  // Funções handle
  const handleClearForm = () => {
    setForm({
      ...form,
      id: 0,
      nObjeto: 0,
      produtoId: 0,
      contratoId: 0,
      nParcelas: 0,
      fatorEmbalagem: '',
      qtdContratada: 0,
      qtdTotal: 0,
      valorUnitario: '',
      valorTotal: '',
      observacoes: '',
      usuarioId: props.userID,
    });
    setLogData({
      ...logData,
      id: 0,
      usuarioRegistro: '',
      usuarioAtualizacao: '',
      dataRegistro: '',
      dataAtualizacao: '',
      nEdicoes: 0,
    });
  };

  const handleAtualizarProxItem = () => {
    if (data && data.length > 0) {
      const numeroItemMax = data[data.length - 1].nObjeto;
      setNumeroProximoItem(numeroItemMax + 1);
    } else {
      setNumeroProximoItem(1);
    }
  };

  // Tabela
  const tableData =
    data?.map((item: IObjetoContrato) => {
      return {
        id: item.id,
        nObjeto: item.nObjeto,
        fatorEmbalagem: item.fatorEmbalagem,
        produtoDAF: item.produtoDAF?.produtoFarmaceutico ?? 'Não informado',
        // parcela: item.parcela ?? 'Não informado',
        valorUnitario: item.valorUnitario
          ? `R$ ${formatValorDinheiro(parseFloat(item.valorUnitario).toFixed(2))}`
          : '-',
        // valorTotal: item.valorTotal ?? 'Não informado',
        // qtdTotal: item.qtdTotal ?? 'Não informado',
      };
    }) || [];

  const offcanvasData =
    data?.map((item: IObjetoContrato) => {
      return {
        id: item.id,
        dataRegistro:
          formatDate(item.dataRegistro ?? '') +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioRegistro?.nomeCompleto ?? ''),
        dataUltimaAtualizacao:
          formatDate(item.dataUltimaAtualizacao ?? '') +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioAtualizacao?.nomeCompleto ?? ''),

        dataUltimaVisualizacao:
          formatDate(item.dataUltimaVisualizacao ?? '') +
          ' - ' +
          formataNomeMeioSiglas(
            item.usuarioUltimaVisualizacao?.nomeCompleto ?? '',
          ),
        nEdicoes: item.nEdicoes,
        nVisualizacoes: item.nVisualizacoes,
        separadorLog: '',
        nObjeto: item.nObjeto,
        produtoDAF: (
          <b>{item.produtoDAF?.produtoFarmaceutico ?? 'Não informado'}</b>
        ),
        fatorEmbalagem: item.fatorEmbalagem,
        // parcela: item.parcela ?? '-',
        valorUnitario: item.valorUnitario ?? '-',
        // valorTotal: item.valorTotal ?? '-',
        // qtdTotal: item.qtdTotal ?? '-',
        separador: '',
        observacoes: item.observacoes,
      };
    }) || [];

  //Funções Handle
  const handleShowModal = () => {
    setShowModal((prev) => !prev);
    handleClearForm();
  };

  const handleClickInserirNovo = () => {
    setUpdating(false);
    handleShowModal();
    handleClearForm();
  };

  const handleRowClick = (id: string) => {
    handleShowModal();
    setUpdating(true);
    const filteredItem = data?.find((item) => item.id == parseInt(id, 10));

    if (filteredItem) {
      let qtdParcelas = 0;
      let qtdContratada = 0;
      let qtdDoada = 0;
      let qtdTotal = 0;
      let valorTotalParcelas = 0.0;
      if (filteredItem.parcelas) {
        qtdParcelas = filteredItem.parcelas.length;

        valorTotalParcelas = filteredItem.parcelas.reduce((total, parcela) => {
          return total + Number(parcela.valorParcela);
        }, 0);

        qtdContratada = filteredItem.parcelas.reduce(
          (accumulator, currentValue) => {
            const qtdContratadaParcela =
              Number(currentValue.qtdAdministrativa) +
              Number(currentValue.qtdEstrategico) +
              Number(currentValue.qtdJudicial);
            return accumulator + qtdContratadaParcela;
          },
          0,
        );

        qtdDoada = filteredItem.parcelas.reduce((accumulator, currentValue) => {
          return accumulator + Number(currentValue.qtdDoada);
        }, 0);

        qtdTotal = qtdContratada + qtdDoada;
      }
      setForm({
        id: filteredItem.id,
        nObjeto: filteredItem.nObjeto,
        produtoDAF: filteredItem.produtoDAF,
        fatorEmbalagem: filteredItem.fatorEmbalagem,
        nParcelas: qtdParcelas,
        valorTotal: `R$ ${formatValorDinheiro(valorTotalParcelas.toString())}`,
        qtdTotal: qtdTotal,
        qtdDoada: qtdDoada,
        qtdContratada: qtdContratada,
        observacoes: filteredItem.observacoes ?? 'Não informado',

        // chave usuário
        usuarioId: props.userID,
        contratoId: filteredItem.contrato?.id,
        produtoId: filteredItem.produtoDAF?.id,
        valorUnitario:
          filteredItem.valorUnitario && filteredItem.valorUnitario != null
            ? parseFloat(filteredItem.valorUnitario).toFixed(4)
            : '',
      });
      setLogData({
        id: filteredItem.id ?? 0,
        usuarioRegistro: filteredItem.usuarioRegistro?.nomeCompleto || '',
        usuarioAtualizacao: filteredItem.usuarioAtualizacao?.nomeCompleto || '',
        dataRegistro: filteredItem.dataRegistro || '',
        dataAtualizacao: filteredItem.dataUltimaAtualizacao || '',
        nEdicoes: filteredItem.nEdicoes || 0,
      });
    }
  };

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target as HTMLInputElement;

    const newValue = value === '' ? 0 : parseInt(value, 10);

    if (name == 'produtoDAF') {
      const produto = produtosDAFOptions.find(
        (option) => option.value.toString() === value,
      );
      if (produto) {
        setForm({
          ...form,
          produtoId: newValue,
        });
      }
      // } else if (name === 'valorUnitario') {
      //   setForm({
      //     ...form,
      //     [name]: formatoMoney(value),
      //   });
    } else if (name === 'nObjeto') {
      setNumeroProximoItem(newValue);
      setForm({
        ...form,
        nObjeto: newValue,
      });
    } else {
      setForm((prev) => {
        const updatedForm = { ...prev, [name]: value };

        return updatedForm;
      });
    }
  };

  const validateForm = (form: IObjetoContrato): boolean => {
    const erros: string[] = [];

    if (!form.fatorEmbalagem) {
      erros.push('<b>Fator Embalagem</b> é obrigatório.');
    }

    if (!form.valorUnitario) {
      erros.push('<b>Valor Unitário</b> é obrigatório.');
    }

    if (!form.produtoId) {
      erros.push('<b>Produto Farmacêutico</b> é obrigatório.');
    }

    if (erros.length > 0) {
      swalAttention(erros.join('<br>'));
      return false;
    }

    return true;
  };

  const formatForm = (form: IObjetoContrato) => {
    const formAjustado = {
      ...form,
      usuarioId: Number(form.usuarioId),
      fatorEmbalagem: Number(form.fatorEmbalagem),
      valorUnitario: parseFloat(form.valorUnitario),
      produtoId: parseInt(form.produtoId.toString(), 10),
      contratoId: Number(props.fichaID),
      nObjeto: numeroProximoItem,
    };

    return formAjustado;
  };

  const handleCreate = async () => {
    try {
      if (!validateForm(form)) throw new Error('Formulário inválido.');
      const formAjustado = formatForm(form);
      const formToSend = sanitizarForm(formAjustado);

      await handleCreateObjetoContrato(formToSend);

      swalSave();
      fetchData();

      setTimeout(() => {
        handleShowModal();
        props.onDataChange();
      }, 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao salvar registro:', err);
    }
  };

  const handleUpdate = async () => {
    try {
      if (!validateForm(form)) throw new Error('Formulário inválido.');
      const formAjustado = formatForm(form);
      const formToSend = sanitizarForm(formAjustado);

      const response = await handleUpdateObjetoContrato(formToSend);

      swalAtualizacao('Sucesso', response.data.message);
      fetchData();

      setTimeout(() => {
        handleShowModal();
        props.onDataChange();
      }, 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao atualizar registro:', err);
    }
  };

  const handleDelete = async () => {
    const result = await swalDelete();
    if (!result) return swalErro('A operação falhou.');
    try {
      if (form.id) {
        await handleDeleteObjetoContrato(form.id);
      } else return;
      swalDeleteConfirm();
      fetchData();

      setTimeout(() => {
        handleShowModal();
        props.onDataChange();
      }, 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao excluir registro:', err);
    }
  };

  return (
    <BoxConteudo
      boxTitulo="Objetos do Contrato"
      fechado={!(props.fichaID !== 'undefined')}
    >
      <CustomRow>
        <CustomCol width="100%">
          <Tabela
            headers={headers}
            dataTable={tableData}
            displayValues={valoresExibidos}
            onRowClick={handleRowClick}
            dataOffCanvas={offcanvasData}
            showPagination={false}
          />
        </CustomCol>
      </CustomRow>
      <CustomCol className="d-flex justify-content-end">
        <CustomButton
          label="Novo objeto"
          bgColor={theme.colors.botaoAcao.azul}
          textColor={theme.colors.white}
          onClick={handleClickInserirNovo}
          disabled={props.fichaID == 'undefined' ? true : false}
        />
      </CustomCol>

      <CustomOffcanvas
        title={'Objetos do contrato'}
        show={showModal}
        onClose={handleShowModal}
        width="70vw"
      >
        <WorkspaceFicha>
          <Log {...logData} entity={tbEntity} id={Number(form.id)} />
          <BoxConteudo boxTitulo={'Objeto'}>
            <CustomRow style={{ marginTop: '10px' }}>
              <CustomCol width="130px">
                <Input
                  name="nObjeto"
                  value={updating ? form.nObjeto : numeroProximoItem}
                  id="nObjeto"
                  label="Nº Objeto"
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol>
                <Select
                  name="produtoId"
                  value={form.produtoId}
                  options={produtosDAFOptions}
                  label="Produto Farmacêutico"
                  onChange={handleChange}
                  required
                  disabled={updating}
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol width="13%">
                <Input
                  name="fatorEmbalagem"
                  value={form.fatorEmbalagem}
                  id="fatorEmbalagem"
                  label="Fator Emb."
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol width="10%">
                <Input
                  name="nParcelas"
                  value={form.nParcelas}
                  id="nParcelas"
                  label="Parcelas"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
              <CustomCol width="15%">
                <Input
                  name="qtdContratada"
                  value={form.qtdContratada ?? ''}
                  id="qtdContratada"
                  label="Qtd Contratada"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
              <CustomCol width="15%">
                <Input
                  name="qtdDoada"
                  value={form.qtdDoada ?? ''}
                  id="qtdDoada"
                  label="Qtd Doada"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
              <CustomCol width="15%">
                <Input
                  name="qtdTotal"
                  value={form.qtdTotal ?? ''}
                  id="qtdTotal"
                  label="Qtd Total"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
              <CustomCol width="13%">
                <InputMoney
                  name="valorUnitario"
                  value={form.valorUnitario}
                  id="valorUnitario"
                  label="Valor Unit R$"
                  casasDecimais={4}
                  onChange={handleChange}
                  textAlign="right"
                  required
                />
              </CustomCol>
              <CustomCol>
                <Input
                  name="valorTotal"
                  value={form.valorTotal ?? ''}
                  id="valorTotal"
                  label="Valor Total - Parcelas"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <Input
                  id="observacoes"
                  type="textarea"
                  name="observacoes"
                  value={form.observacoes}
                  label="Observações"
                  linhas={2}
                  onChange={handleChange}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>
          <BarraInferiorBotoes>
            {updating ? (
              <WorkspaceBotoes
                delete
                deleteDisabled={props.btDisable}
                onClickDelete={handleDelete}
                save
                saveDisabled={props.btDisable}
                onClickSave={handleUpdate}
              />
            ) : (
              <WorkspaceBotoes
                inserir
                inserirDisabled={props.btDisable}
                onClickInserir={handleCreate}
              />
            )}
          </BarraInferiorBotoes>
        </WorkspaceFicha>
      </CustomOffcanvas>
    </BoxConteudo>
  );
};
